/* eslint-disable react/prop-types */
// ---------------------------------------------------------------------------
// ProgressDashboardModal — Cockpit de saude da modelagem (V3).
// ---------------------------------------------------------------------------
// Modal grande que substitui a antiga "pendencias em aberto" por uma visao
// cockpit do mes selecionado: 4 KPIs, barra de progresso e dois paineis
// roláveis (Acoes necessarias + Linha do tempo do processamento).
//
// Protocolo atual: mock completo (padrao frontend-mock-first do V3).
// O conteudo estrutural (acoes, timeline) sera plugado ao backend em
// iteracoes seguintes; o papel deste componente aqui é ancorar a UI.
//
// Scroll: listas internas usam `useOverlayScrollStyles` + `useNativeWheelScroll`
// para que o wheel do mouse role o painel (e nao "vaze" para ancestrais),
// seguindo o mesmo padrao do card de status por responsavel.
// ---------------------------------------------------------------------------

import React from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";

import {
  grayColor,
  whiteColor,
  blendColors,
} from "../../../assets/jss/material-dashboard-pro-react";
import ModalHeader from "../../../components/ModalHeader";
import { Transition } from "../inputs_components";
import useNativeWheelScroll from "./hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "./styles";

// Cores semanticas usadas dentro do modal. Mantemos alinhadas com o HTML
// prototipo (sandbox/inputs_v3_progress_dashboard.html) e com a paleta
// compartilhada da aplicacao.
const DONE = "#2e7d32"; // verde "finalizado"
const STALE = "#8bc34a"; // verde claro "defasado"
const ACTION = "#c62828"; // vermelho "acao"
const WAITING = "#c7cbd1"; // cinza claro "aguardando"
const DECISION = "#ef6c00"; // laranja "decisao"
const INFO = "#1565c0"; // azul "sistema"
// Backgrounds dos chips (fundo claro com tom sutil da cor base).
// blendColors(A, B, alpha) = peso A * alpha + peso B * (1 - alpha).
// Queremos 85% branco + 15% da cor → alpha 0.15 no primeiro arg = branco? Não.
// Como A tem peso alpha, para fundo claro: blendColors(COLOR, "#fff", 0.15).
const SOFT_BG = (color) => blendColors(color, whiteColor, 0.15);

const useStyles = makeStyles(() => ({
  // ---------- Dialog shell ----------
  paper: {
    borderRadius: 12,
    maxHeight: "92vh",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    background: "#353534",
    flexShrink: 0,
  },
  headerStrip: {
    width: 4,
    height: 28,
    borderRadius: 2,
    flexShrink: 0,
  },
  headerTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: "-0.01em",
    color: whiteColor,
  },
  headerMonthValue: {
    marginLeft: "auto",
    fontSize: 14,
    fontWeight: 500,
    color: whiteColor,
    letterSpacing: "-0.01em",
  },
  closeBtn: {
    padding: 6,
    marginLeft: 4,
    color: whiteColor,
  },

  // ---------- Body ----------
  body: {
    padding: 12,
    overflow: "auto",
    maxHeight: "calc(92vh - 60px)",
    background: whiteColor,
  },

  // ---------- KPIs ----------
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 12,
    marginBottom: 12,
  },
  kpi: {
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 8,
    padding: "14px 16px",
    background: grayColor[19] + "bb",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  kpiLabel: {
    fontSize: 10.5,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: grayColor[1],
  },
  kpiValue: {
    fontSize: 26,
    fontWeight: 400,
    color: grayColor[2],
    letterSpacing: "-0.015em",
    lineHeight: 1.15,
  },
  kpiValueTotal: {
    color: grayColor[0],
    fontSize: 16,
    fontWeight: 300,
    marginLeft: 2,
  },
  kpiHint: {
    fontSize: 11,
    color: grayColor[1],
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 2,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "1px 6px",
    borderRadius: 999,
    fontSize: 10,
  },
  chipDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },

  // ---------- progress row ----------
  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 12,
    padding: "12px 16px",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 8,
    background: whiteColor,
  },
  progressLabel: {
    fontSize: 11,
    color: grayColor[1],
    minWidth: 110,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    background: grayColor[12],
    overflow: "hidden",
    display: "flex",
  },
  progressLegend: {
    fontSize: 11,
    color: grayColor[1],
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  // ---------- panels grid ----------
  cols: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr",
    gap: 12,
  },
  panel: {
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 8,
    background: whiteColor,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: 460,
  },
  panelHead: {
    padding: "12px 16px",
    borderBottom: `1px solid ${grayColor[12]}`,
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    flexShrink: 0,
  },
  panelHeadTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 500,
    color: grayColor[2],
  },
  panelHeadCount: {
    marginLeft: "auto",
    fontSize: 11,
    color: grayColor[1],
  },
  panelBody: {
    flex: 1,
    minHeight: 0,
  },

  // ---------- action item ----------
  actionItem: {
    padding: "10px 16px",
    display: "grid",
    gridTemplateColumns: "28px 1fr 140px 60px",
    alignItems: "center",
    gap: 12,
    borderBottom: `1px solid ${grayColor[12]}`,
    cursor: "pointer",
    transition: "background 120ms",
    "&:hover": { background: grayColor[13] },
    "&:last-child": { borderBottom: "none" },
  },
  actionIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 500,
  },
  actionBody: { minWidth: 0 },
  actionTitle: { fontSize: 13, color: grayColor[2], fontWeight: 400 },
  actionMeta: {
    fontSize: 11,
    color: grayColor[1],
    marginTop: 2,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  actionPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "1px 6px",
    borderRadius: 999,
    background: grayColor[12],
    fontSize: 10,
  },
  actionResp: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: grayColor[1],
    minWidth: 0,
  },
  actionRespAvatar: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9.5,
    fontWeight: 500,
  },
  actionRespName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: grayColor[2],
    fontWeight: 400,
  },
  actionAge: {
    fontSize: 11,
    color: grayColor[1],
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  actionAgeStale: { color: ACTION },

  // ---------- timeline event ----------
  events: { padding: "4px 0" },
  event: {
    padding: "8px 16px",
    display: "grid",
    gridTemplateColumns: "72px 10px 1fr",
    columnGap: 10,
    alignItems: "flex-start",
    borderBottom: `1px solid ${grayColor[12]}`,
    "&:last-child": { borderBottom: "none" },
  },
  eventTime: {
    fontSize: 10.5,
    color: grayColor[0],
    paddingTop: 2,
    textAlign: "right",
  },
  eventDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginTop: 6,
    justifySelf: "center",
  },
  eventText: { fontSize: 12, color: grayColor[2], minWidth: 0 },
  eventWho: { color: grayColor[2], fontWeight: 400 },
  eventWhat: { color: grayColor[1] },
  eventFuture: {
    "& $eventTime": { color: grayColor[0], fontStyle: "italic" },
    "& $eventText": { color: grayColor[1] },
    "& $eventWho": { color: grayColor[1], fontWeight: 300 },
    "& $eventWhat": { color: grayColor[1] },
  },
  eventWaiting: {
    "& $eventTime": {
      fontStyle: "normal",
      color: grayColor[0],
      fontSize: 10,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
  },
  eventDotWaiting: {
    background: whiteColor,
    boxShadow: `inset 0 0 0 1.5px ${grayColor[6]}`,
  },
  nowMarker: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  nowTime: {
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 10,
  },
}));

// ------------------------------------------------------------------
// Mock data — replica o protótipo sandbox/inputs_v3_progress_dashboard.html
// Deve ser substituído pela integração backend em iteração posterior.
// ------------------------------------------------------------------
const MOCK_ACTIONS = [
  {
    id: "a1",
    type: "decision",
    title:
      "Decidir ajuste no cruzamento de <strong>Vendas Diretas × Centralização</strong>",
    meta: "2,5% das vendas não cruzam",
    resp: { name: "Jairo Pakes", initials: "JP", kind: "user" },
    age: "há 3d",
    stale: true,
  },
  {
    id: "a2",
    type: "decision",
    title: "Decidir tratamento de <strong>promoções não mapeadas</strong>",
    meta: "187 SKUs fora do catálogo",
    resp: { name: "Maria C.", initials: "MC", kind: "user" },
    age: "há 2d",
    stale: true,
  },
  {
    id: "a3",
    type: "decision",
    title: 'Decidir sobre novo canal <strong>"E-commerce B2B"</strong>',
    meta: "não cadastrado na dimensão Canal",
    resp: { name: "Jairo Pakes", initials: "JP", kind: "user" },
    age: "hoje",
  },
  {
    id: "a4",
    type: "upload",
    title: "<strong>3 inputs</strong> pendentes de envio",
    meta: "ICP Chile · Estoques · Fretes Primários",
    resp: { name: "Ana Pérez", initials: "AP", kind: "user" },
    age: "há 4d",
    stale: true,
  },
  {
    id: "a5",
    type: "upload",
    title: "<strong>2 inputs</strong> pendentes de envio",
    meta: "Devoluções · Ajustes de estoque",
    resp: { name: "Maria C.", initials: "MC", kind: "user" },
    age: "há 1d",
  },
  {
    id: "a6",
    type: "upload",
    title: "<strong>1 input</strong> pendente de envio",
    meta: "Preços Praticados",
    resp: { name: "Carlos López", initials: "CL", kind: "user" },
    age: "hoje",
  },
  {
    id: "a7",
    type: "error",
    title: "Erro de execução no bloco <strong>Custos logísticos</strong>",
    meta: "bloqueia os blocos dependentes · detalhes técnicos disponíveis ao DEV",
    resp: { name: "Integration", initials: "IN", kind: "integration" },
    age: "há 2h",
    stale: true,
  },
  {
    id: "a8",
    type: "recalc",
    title:
      "Recálculo pendente após ajuste de <strong>opções especiais</strong>",
    meta: "4 inputs alterados · cálculo ficou stale",
    resp: { name: "Integration", initials: "IN", kind: "integration" },
    age: "hoje",
  },
];

const MOCK_EVENTS = [
  {
    kind: "future-waiting",
    time: "aguardando",
    who: "Bloco",
    what: " Margem por cliente · aguarda P&L consolidado",
  },
  {
    kind: "future-waiting",
    time: "aguardando",
    who: "Bloco",
    what: " P&L consolidado · aguarda conclusão de Custos logísticos",
  },
  {
    kind: "now",
    time: "agora",
    content: "14:22 · 17 abr 2026",
  },
  {
    kind: "past",
    dot: ACTION,
    time: "há 2 h",
    who: "Bloco Custos logísticos",
    what: " falhou — cálculo interrompido, blocos dependentes foram para a fila",
  },
  {
    kind: "past",
    dot: "#ef6c00",
    time: "há 2 h",
    who: "Sistema",
    what: " abriu decisão sobre Vendas Diretas × Centralização (2,5% sem cruzamento)",
  },
  {
    kind: "past",
    dot: DONE,
    time: "há 3 h",
    who: "Bloco Descontos e devoluções",
    what: " concluído em 42s",
  },
  {
    kind: "past",
    dot: DONE,
    time: "há 3 h",
    who: "Bloco Vendas brutas",
    what: " concluído em 1m18s",
  },
  {
    kind: "past",
    dot: DONE,
    time: "há 3 h",
    who: "Bloco Dimensões",
    what: " concluído em 24s",
  },
  {
    kind: "past",
    dot: "#1565c0",
    time: "há 3 h",
    who: "Sistema",
    what: " iniciou cálculo incremental (12 blocos)",
  },
  {
    kind: "past",
    dot: "#ef6c00",
    time: "há 5 h",
    who: "Sistema",
    what: " abriu decisão sobre promoções não mapeadas (187 SKUs)",
  },
  {
    kind: "past",
    dot: grayColor[0],
    time: "há 6 h",
    who: "Sistema",
    what: " reprocessou Vendas Diretas após novo upload",
  },
  {
    kind: "past",
    dot: DONE,
    time: "ontem",
    who: "Bloco P&L consolidado",
    what: " concluído em 3m44s — 1º cálculo do mês OK",
  },
];

// ------------------------------------------------------------------
// Subcomponentes
// ------------------------------------------------------------------
function KpiChip({ classes, color, bg, label }) {
  return (
    <span className={classes.chip} style={{ color, background: bg }}>
      <span className={classes.chipDot} style={{ background: color }} />
      {label}
    </span>
  );
}

function ActionItem({ classes, action, clientColor }) {
  const iconStyle = {
    decision: { bg: "#fff3e0", color: DECISION, symbol: "?" },
    upload: { bg: SOFT_BG(ACTION), color: ACTION, symbol: "↑" },
    error: { bg: "#e3f2fd", color: INFO, symbol: "!" },
    recalc: { bg: SOFT_BG(clientColor), color: clientColor, symbol: "↻" },
  }[action.type];

  const respStyle =
    action.resp.kind === "integration"
      ? { bg: grayColor[8], color: grayColor[2] }
      : { bg: SOFT_BG(clientColor), color: clientColor };

  return (
    <div className={classes.actionItem}>
      <div
        className={classes.actionIcon}
        style={{ background: iconStyle.bg, color: iconStyle.color }}
      >
        {iconStyle.symbol}
      </div>
      <div className={classes.actionBody}>
        <div
          className={classes.actionTitle}
          dangerouslySetInnerHTML={{ __html: action.title }}
        />
        <div className={classes.actionMeta}>
          <span className={classes.actionPill}>
            {action.type === "decision"
              ? "decisão"
              : action.type === "upload"
                ? "envio"
                : action.type === "error"
                  ? "erro"
                  : "defasado"}
          </span>
          <span>{action.meta}</span>
        </div>
      </div>
      <div className={classes.actionResp}>
        <span
          className={classes.actionRespAvatar}
          style={{ background: respStyle.bg, color: respStyle.color }}
        >
          {action.resp.initials}
        </span>
        <span className={classes.actionRespName}>{action.resp.name}</span>
      </div>
      <span
        className={classes.actionAge}
        style={action.stale ? { color: ACTION } : undefined}
      >
        {action.age}
      </span>
    </div>
  );
}

function EventItem({ classes, event, clientColor, clientColorSoft }) {
  if (event.kind === "now") {
    return (
      <div
        className={`${classes.event} ${classes.nowMarker}`}
        style={{
          background: blendColors(clientColor, whiteColor, 0.04),
          borderTop: `1px solid ${blendColors(clientColor, whiteColor, 0.08)}`,
          borderBottom: `1px solid ${blendColors(clientColor, whiteColor, 0.08)}`,
        }}
      >
        <span
          className={`${classes.eventTime} ${classes.nowTime}`}
          style={{ color: clientColor, fontStyle: "normal" }}
        >
          {event.time}
        </span>
        <span
          className={classes.eventDot}
          style={{
            background: clientColor,
            boxShadow: `0 0 0 3px ${clientColorSoft}`,
          }}
        />
        <div className={classes.eventText}>
          <strong style={{ color: grayColor[2], fontSize: 11.5 }}>
            {event.content}
          </strong>
        </div>
      </div>
    );
  }
  if (event.kind === "future-waiting") {
    return (
      <div
        className={`${classes.event} ${classes.eventFuture} ${classes.eventWaiting}`}
      >
        <span className={classes.eventTime}>{event.time}</span>
        <span className={`${classes.eventDot} ${classes.eventDotWaiting}`} />
        <div className={classes.eventText}>
          <span className={classes.eventWhat}>{event.who}</span>
          <span className={classes.eventWho}>{event.what}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={classes.event}>
      <span className={classes.eventTime}>{event.time}</span>
      <span className={classes.eventDot} style={{ background: event.dot }} />
      <div className={classes.eventText}>
        <span className={classes.eventWho}>{event.who}</span>
        <span className={classes.eventWhat}>{event.what}</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Componente principal
// ------------------------------------------------------------------
export default function ProgressDashboardModal({
  open,
  onClose,
  clientColor,
  selectedMonthLabel,
  monthsImplementation = { done: 14, stale: 4, total: 30 },
  inputsSent = { sent: 23, action: 3, waiting: 8, total: 34 },
  actionsCount = {
    decisions: 3,
    uploads: 3,
    errors: 1,
    recalcs: 1,
  },
  queueCount = 0,
  queueWaiting = 2,
  progress = { done: 47, stale: 13, action: 10, waiting: 30 },
  actions = MOCK_ACTIONS,
  events = MOCK_EVENTS,
}) {
  const classes = useStyles();
  const overlayScroll = useOverlayScrollStyles();
  const actionsScrollRef = useNativeWheelScroll();
  const eventsScrollRef = useNativeWheelScroll();

  const clientColorSoft = blendColors(clientColor, whiteColor, 0.08);

  const totalActions =
    actionsCount.decisions +
    actionsCount.uploads +
    actionsCount.errors +
    actionsCount.recalcs;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.paper }}
    >
      <ModalHeader
        title="Progresso da modelagem"
        subtitle="Visao consolidada da implementacao"
        pills={
          selectedMonthLabel
            ? [{ key: "month", label: selectedMonthLabel }]
            : []
        }
        onClose={onClose}
        closeLabel="Fechar"
        clientColor={clientColor}
      />

      <div className={classes.body}>
        {/* KPIs */}
        <div className={classes.kpis}>
          <div className={classes.kpi}>
            <span className={classes.kpiLabel}>Meses da implementação</span>
            <span className={classes.kpiValue}>
              {monthsImplementation.done + monthsImplementation.stale}
              <span className={classes.kpiValueTotal}>
                {" / " + monthsImplementation.total}
              </span>
            </span>
            <span className={classes.kpiHint}>
              <KpiChip
                classes={classes}
                color={DONE}
                bg={SOFT_BG(DONE)}
                label={`${monthsImplementation.done} finalizados`}
              />
              <KpiChip
                classes={classes}
                color="#558b2f"
                bg={SOFT_BG(STALE)}
                label={`${monthsImplementation.stale} defasados`}
              />
            </span>
          </div>

          <div className={classes.kpi}>
            <span className={classes.kpiLabel}>Inputs enviados</span>
            <span className={classes.kpiValue}>
              {inputsSent.sent}
              <span className={classes.kpiValueTotal}>
                {" / " + inputsSent.total}
              </span>
            </span>
            <span className={classes.kpiHint}>
              <KpiChip
                classes={classes}
                color={ACTION}
                bg={SOFT_BG(ACTION)}
                label={`${inputsSent.action} ação`}
              />
              <KpiChip
                classes={classes}
                color={grayColor[1]}
                bg={grayColor[8]}
                label={`${inputsSent.waiting} aguardando`}
              />
            </span>
          </div>

          <div className={classes.kpi}>
            <span className={classes.kpiLabel}>Ações necessárias</span>
            <span className={classes.kpiValue}>{totalActions}</span>
            <span className={classes.kpiHint}>
              {`${actionsCount.decisions} decisões · ${actionsCount.uploads} envios · ${actionsCount.errors} erro · ${actionsCount.recalcs} recálculo`}
            </span>
          </div>

          <div className={classes.kpi}>
            <span className={classes.kpiLabel}>Cálculos na fila</span>
            <span className={classes.kpiValue}>{queueCount}</span>
            <span className={classes.kpiHint}>
              {`${queueCount} na fila · ${queueWaiting} aguardando dados`}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className={classes.progressRow}>
          <div className={classes.progressLabel}>Progresso do mês</div>
          <div className={classes.progressBar}>
            <div style={{ width: `${progress.done}%`, background: DONE }} />
            <div style={{ width: `${progress.stale}%`, background: STALE }} />
            <div style={{ width: `${progress.action}%`, background: ACTION }} />
            <div
              style={{ width: `${progress.waiting}%`, background: WAITING }}
            />
          </div>
          <div className={classes.progressLegend}>
            <KpiChip
              classes={classes}
              color={DONE}
              bg={SOFT_BG(DONE)}
              label={`${progress.done}%`}
            />
            <KpiChip
              classes={classes}
              color="#558b2f"
              bg={SOFT_BG(STALE)}
              label={`${progress.stale}%`}
            />
            <KpiChip
              classes={classes}
              color={ACTION}
              bg={SOFT_BG(ACTION)}
              label={`${progress.action}%`}
            />
            <KpiChip
              classes={classes}
              color={grayColor[1]}
              bg={grayColor[8]}
              label={`${progress.waiting}%`}
            />
          </div>
        </div>

        {/* Painéis */}
        <div className={classes.cols}>
          {/* Ações necessárias */}
          <div className={classes.panel}>
            <div className={classes.panelHead}>
              <h4 className={classes.panelHeadTitle}>Ações necessárias</h4>
              <span className={classes.panelHeadCount}>
                {totalActions} no total
              </span>
            </div>
            <div
              ref={actionsScrollRef}
              className={`${classes.panelBody} ${overlayScroll.overlayScroll}`}
            >
              {actions.map((a) => (
                <ActionItem
                  key={a.id}
                  classes={classes}
                  action={a}
                  clientColor={clientColor}
                />
              ))}
            </div>
          </div>

          {/* Linha do tempo */}
          <div className={classes.panel}>
            <div className={classes.panelHead}>
              <h4 className={classes.panelHeadTitle}>
                Linha do tempo do processamento
              </h4>
              <span className={classes.panelHeadCount}>
                {selectedMonthLabel || "—"}
              </span>
            </div>
            <div
              ref={eventsScrollRef}
              className={`${classes.panelBody} ${overlayScroll.overlayScroll}`}
            >
              <div className={classes.events}>
                {events.map((e, i) => (
                  <EventItem
                    key={i}
                    classes={classes}
                    event={e}
                    clientColor={clientColor}
                    clientColorSoft={clientColorSoft}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
